import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { FormInput } from './forms/FormInput';
import { FormStatus } from './forms/FormStatus';

interface SignupFormProps {
  onSubmitSuccess?: () => void;
  selectedPlan: string;
}

export default function SignupForm({ onSubmitSuccess, selectedPlan }: SignupFormProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [errorMessage, setErrorMessage] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    email: '',
    birthDate: '',
    emergencyContact: '',
    healthInfo: '',
    statutesAccepted: false,
    regulationsAccepted: false,
    communicationAccepted: false
  });

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (formSubmitted && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      navigate('/');
    }
    return () => clearTimeout(timer);
  }, [formSubmitted, countdown, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    
    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [target.name]: target.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [target.name]: target.value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');
    
    const payload = {
      name: formData.name,
      phoneNumber: formData.phoneNumber,
      email: formData.email,
      birthDate: formData.birthDate,
      emergencyContact: formData.emergencyContact,
      healthInfo: formData.healthInfo,
      statutesAccepted: formData.statutesAccepted,
      regulationsAccepted: formData.regulationsAccepted,
      communicationAccepted: formData.communicationAccepted,
      plan: selectedPlan
    };

    try {
      const apiUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, ''); // Remove trailing slash if present
      
      // Development mode: simulate successful submission
      if (!apiUrl) {
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        setSubmitStatus('success');
        setFormSubmitted(true);
        onSubmitSuccess?.();
        return;
      }
      
      const endpoint = `${apiUrl}/api/signup`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error:', errorText);
        throw new Error(`Network response was not ok: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      
      if (data) {
        setSubmitStatus('success');
        setFormSubmitted(true);
        onSubmitSuccess?.();
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Er is een onbekende fout opgetreden.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return formSubmitted ? (
    <div className="p-8 bg-green-100 rounded-lg text-center">
      <h2 className="text-2xl font-bold text-green-700 mb-4">
        Bedankt voor je aanmelding!
      </h2>
      <p className="text-green-600 mb-6">
        We hebben je aanmelding ontvangen en nemen zo spoedig mogelijk contact met je op.
      </p>
      <div className="space-y-4">
        <button
          onClick={() => navigate('/')}
          className="text-[#D7790F] hover:text-[#A91A18] transition-colors"
        >
          Klik hier om terug te keren naar de homepage
        </button>
        <p className="text-sm text-green-600">
          Je wordt automatisch doorgestuurd in {countdown} seconden
        </p>
      </div>
    </div>
  ) : (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
      <div className="space-y-6">
        <FormInput
          id="name"
          name="name"
          label="Naam"
          type="text"
          value={formData.name}
          onChange={handleChange}
          required
        />

        <FormInput
          id="phoneNumber"
          name="phoneNumber"
          label="Telefoonnummer"
          type="tel"
          value={formData.phoneNumber}
          onChange={handleChange}
          required
        />

        <FormInput
          id="email"
          name="email"
          label="Email-adres"
          type="email"
          value={formData.email}
          onChange={handleChange}
          required
        />

        <FormInput
          id="birthDate"
          name="birthDate"
          label="Geboortedatum"
          type="date"
          value={formData.birthDate}
          onChange={handleChange}
          required
        />

        <FormInput
          id="emergencyContact"
          name="emergencyContact"
          label="Telefoonnummer contactpersoon in geval van nood"
          type="tel"
          value={formData.emergencyContact}
          onChange={handleChange}
          required
        />

        <div className="space-y-1">
          <label htmlFor="healthInfo" className="block text-sm font-medium text-gray-700">
            Zijn er gezondheidsaspecten waarvan het verstandig is dat de docent op de hoogte is, vul die hieronder in
          </label>
          <textarea
            id="healthInfo"
            name="healthInfo"
            rows={3}
            value={formData.healthInfo}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:ring-[#D7790F] focus:border-[#D7790F]"
          />
        </div>

        <div className="space-y-4 pt-4">
          <h3 className="font-medium">Voorwaarden en communicatie <span className="italic">(bij aanmelding voor een proefles of introductiecursus hoeft alleen de onderste aangevinkt te worden)</span></h3>
          
          <FormInput
            id="statutesAccepted"
            name="statutesAccepted"
            label="Ik ga akkoord met de statuten"
            type="checkbox"
            checked={formData.statutesAccepted}
            onChange={handleChange}
            value={formData.statutesAccepted.toString()}
          />

          <FormInput
            id="regulationsAccepted"
            name="regulationsAccepted"
            label="Ik ga akkoord met het huishoudelijk reglement"
            type="checkbox"
            checked={formData.regulationsAccepted}
            onChange={handleChange}
            value={formData.regulationsAccepted.toString()}
          />

          <FormInput
            id="communicationAccepted"
            name="communicationAccepted"
            label="Ik ga ermee akkoord dat bovenstaand email-adres en 06-nummer worden gebruikt voor communicatie vanuit de vereniging"
            type="checkbox"
            checked={formData.communicationAccepted}
            onChange={handleChange}
            required
            value={formData.communicationAccepted.toString()}
            title="Vink dit vakje aan om door te gaan"
          />

        </div>
      </div>

      <FormStatus 
        status={submitStatus}
        successMessage="Registratie succesvol! Je wordt doorgestuurd naar de volgende stap."
        errorMessage={errorMessage || "Er is een fout opgetreden bij het registreren. Probeer het opnieuw."}
      />

      <button 
        type="submit"
        disabled={isSubmitting}
        className="btn-primary"
      >
        {isSubmitting ? 'Bezig met registreren...' : 'Registreren'}
      </button>
    </form>
  );
}